import React, { FormEvent, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
  InputNumber,
  InputNumberValueChangeEvent,
} from 'primereact/inputnumber';
import { DropdownField, InputTextField, InputNumberField } from '@/components';
import { useTranslation } from 'react-i18next';
import useToastNotification from '@/hooks/useToastNotification';
import { useMutation } from '@tanstack/react-query';
import { generateCfe } from './generateCfe';
import DownloadPdf from './DownloadPdf/DownloadPdf';
import styles from './Cfe.module.scss';
import EFacturaImg from './e-factura.png';

export interface EFacturaFormState {
  typeCFE: number | null;
  serieCFE: string;
  number: number | null;
  amount: number | null;
  securityCode: string;
}

interface EFacturaFormErrors {
  typeCFE: string;
  serieCFE: string;
  number: string;
  amount: string;
  securityCode: string;
}

const Cfe: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { t } = useTranslation('cfe');
  const { showToast } = useToastNotification();
  const serieCFERef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<InputNumber>(null);
  const typeRef = useRef<Dropdown>(null);
  const securityCodeRef = useRef<HTMLInputElement>(null);
  const documentTypes = [
    { name: t('cfeDropdown.eTicket'), value: 101 },
    { name: t('cfeDropdown.creditNoteOfETicket'), value: 102 },
    { name: t('cfeDropdown.debitNoteOfETicket'), value: 103 },
    { name: t('cfeDropdown.eInvoice'), value: 111 },
    { name: t('cfeDropdown.creditNoteOfEInvoice'), value: 112 },
    { name: t('cfeDropdown.debitNoteOfEInvoice'), value: 113 },
  ];

  const [cfeWasSent, setCfeWasSent] = useState(false);

  const [formData, setFormData] = useState<EFacturaFormState>({
    typeCFE: documentTypes[3].value,
    serieCFE: 'A',
    number: null,
    amount: null,
    securityCode: '',
  });

  const [formErrors, setFormErrors] = useState<EFacturaFormErrors>({
    typeCFE: '',
    serieCFE: '',
    number: '',
    amount: '',
    securityCode: '',
  });

  const [pdfData, setPdfData] = useState<string | null>(null);

  const generateAnotherPdf = () => {
    setFormData({
      typeCFE: null,
      serieCFE: '',
      number: null,
      amount: null,
      securityCode: '',
    });
    setCfeWasSent(false);
  };

  const generateCfeMutation = useMutation({
    mutationFn: generateCfe,
    onSuccess: (response) => {
      setPdfData(response.data.value);
      setCfeWasSent(true);
    },
    onError: (error) => {
      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('cfeFormValidations.unexpectedError'),
        });
      }
      generateAnotherPdf();
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeEvent,
    name: string
  ) => {
    const { value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    let errors: EFacturaFormErrors = { ...formErrors };

    if (!formData.typeCFE) {
      errors = { ...errors, typeCFE: t('cfeFormValidations.typeCFERequired') };
    }
    if (!formData.serieCFE) {
      errors = {
        ...errors,
        serieCFE: t('cfeFormValidations.seriesCFERequired'),
      };
    }
    if (!formData.number) {
      errors = { ...errors, number: t('cfeFormValidations.numberRequired') };
    }
    if (!formData.amount) {
      errors = { ...errors, amount: t('cfeFormValidations.amountRequired') };
    }
    if (!formData.securityCode) {
      errors = {
        ...errors,
        securityCode: t('cfeFormValidations.securityCodeRequired'),
      };
    }

    setFormErrors(errors);
    return Object.values(errors).every((x) => x === '');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm();
    if (!executeRecaptcha) {
      showToast({
        severity: 'error',
        detail: t('cfeFormValidations.reCaptcha'),
      });
      return;
    }

    const reCaptchaToken = await executeRecaptcha();

    if (!reCaptchaToken) {
      showToast({
        severity: 'error',
        detail: t('cfeFormValidations.reCaptcha'),
      });
      return;
    }

    if (validateForm()) {
      generateCfeMutation.mutate(formData);
    }
  };

  const handleDropdownChange = (e: { value: string | null }, name: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: e.value,
    }));
  };

  const downloadPdf = () => {
    if (!pdfData) return;

    const byteCharacters = atob(pdfData.replace(/-/g, '+').replace(/_/g, '/'));
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = 'downloaded_cfe.pdf';
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const focusInputField = (fieldName: string) => {
    switch (fieldName) {
      case 'serieCFE':
        serieCFERef.current?.focus();
        break;
      case 'number':
        numberRef.current?.focus();
        break;
      case 'type':
        typeRef.current?.focus();
        break;
      case 'securityCode':
        securityCodeRef.current?.focus();
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.whiteBg}>
        <form onSubmit={handleSubmit} className={styles.cfeForm}>
          <header>
            <div>
              <h3>{t('title')}</h3>
              <p className={styles.subtitle}>{t('subtitle')}</p>
            </div>
            <div />
          </header>
          <DropdownField
            id="typeCFE"
            label={t('billingType')}
            options={documentTypes}
            value={formData.typeCFE}
            onChange={(e) => handleDropdownChange(e, 'typeCFE')}
            error={formErrors.typeCFE}
            placeholder={t('billingTypePlacheholder')}
            className={styles.dropdown}
            ref={typeRef}
          />

          <InputTextField
            id="serieCFE"
            name="serieCFE"
            value={formData.serieCFE}
            onChange={(e) => handleInputChange(e, 'serieCFE')}
            placeholder={`${t('insertThe')} ${t('series')}`}
            label={t('series')}
            error={formErrors.serieCFE}
            ref={serieCFERef}
          />

          <InputNumberField
            id="number"
            name="number"
            value={formData.number}
            onChange={(e) => handleInputChange(e, 'number')}
            placeholder={`${t('insertThe')} ${t('billingNumber')}`}
            label={t('billingNumber')}
            error={formErrors.number}
            ref={numberRef}
          />

          <InputNumberField
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={(e) => handleInputChange(e, 'amount')}
            placeholder={t('amount')}
            label={t('amount')}
            error={formErrors.amount}
          />

          <InputTextField
            id="securityCode"
            name="securityCode"
            value={formData.securityCode}
            onChange={(e) => handleInputChange(e, 'securityCode')}
            placeholder={t('securityCode')}
            label={t('securityCode')}
            error={formErrors.securityCode}
            ref={securityCodeRef}
          />

          <Button
            type="submit"
            className={styles.submitButton}
            severity="success"
            disabled={generateCfeMutation.isPending}
          >
            {t('getBillingButton')}
          </Button>
          {cfeWasSent && (
            <DownloadPdf
              generateAnotherPdf={generateAnotherPdf}
              downloadPdf={downloadPdf}
            />
          )}
        </form>
        <div className={styles.imgContainer}>
          <img
            src={EFacturaImg}
            alt="Description"
            className={styles.imgInstructions}
          />
          <button
            type="button"
            className={styles.invisibleButton}
            onClick={() => focusInputField('type')}
            aria-label="Focus TypeCfe Input"
          />
          <button
            type="button"
            className={styles.invisibleButton}
            onClick={() => focusInputField('number')}
            aria-label="Focus Number Input"
          />
          <button
            type="button"
            className={styles.invisibleButton}
            onClick={() => focusInputField('serieCFE')}
            aria-label="Focus Series Input"
          />
          <button
            type="button"
            className={styles.invisibleButton}
            onClick={() => focusInputField('securityCode')}
            aria-label="Focus Security Code Input"
          />
        </div>
      </div>
    </div>
  );
};
export default Cfe;
